import { useCallback, useEffect, useRef, useState } from 'react'
import type { DragEvent, MouseEvent } from 'react'

import classNames from 'classnames'

import { useHorizontalDragScroll, useHorizontalOverflow } from '@app/hooks'

import { Image as AttachmentImage } from '@lib/components'
import { ImageTemplate } from '@lib/components/Image/types'

import {
  downloadAttachmentAsObjectUrl,
  openAttachmentWithAuth,
} from '../attachmentAuth'
import sharedStyles from '../styles.module.scss'
import { filesApiPattern, MIN_IMAGE_ATTACHMENT_SIZE } from './constants'
import { buildPreviewUrl, getImageConstructor } from './helpers'
import styles from './styles.module.scss'
import type { MessageImageAttachmentsProps } from './types'
import type { FileAttachmentItem } from '../types'

export const MessageImageAttachments = ({ items }: MessageImageAttachmentsProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const previewUrlsRef = useRef<Record<string, string>>({})
  const excludedAttachmentIdsRef = useRef<Set<string>>(new Set())
  const [, triggerRerender] = useState(0)

  const visibleItems = items.filter(
    (item) => !excludedAttachmentIdsRef.current.has(item.id)
  )
  const hasHorizontalOverflow = useHorizontalOverflow(containerRef, [visibleItems.length])

  useHorizontalDragScroll(containerRef, {
    disabled: !hasHorizontalOverflow,
  })

  const handleImageAnchorDragStart = useCallback(
    (event: DragEvent<HTMLAnchorElement>) => {
      event.preventDefault()
    },
    []
  )

  const handleImageAnchorClick = useCallback(
    (item: FileAttachmentItem) => async (event: MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation()
      event.preventDefault()

      try {
        await openAttachmentWithAuth(item.url)
      } catch (error) {
        console.error('Failed to open image attachment', error)
      }
    },
    []
  )

  useEffect(() => {
    const nextIds = new Set(items.map((item) => item.id))

    Object.entries(previewUrlsRef.current).forEach(([id, url]) => {
      if (!nextIds.has(id)) {
        URL.revokeObjectURL(url)
        delete previewUrlsRef.current[id]
      }
    })

    Array.from(excludedAttachmentIdsRef.current).forEach((id) => {
      if (!nextIds.has(id)) {
        excludedAttachmentIdsRef.current.delete(id)
      }
    })

    const controllers = new Map<string, AbortController>()

    items.forEach((item) => {
      if (
        previewUrlsRef.current[item.id] ||
        excludedAttachmentIdsRef.current.has(item.id)
      ) {
        return
      }

      const controller = new AbortController()
      controllers.set(item.id, controller)

      const template: ImageTemplate = filesApiPattern.test(item.url)
        ? 'listing-sm'
        : 'external'

      const previewUrl = buildPreviewUrl(item.url, template)

      downloadAttachmentAsObjectUrl(previewUrl, controller.signal)
        .then((objectUrl) => {
          const ImageConstructor = getImageConstructor()
          if (!ImageConstructor) {
            excludedAttachmentIdsRef.current.add(item.id)
            triggerRerender((value) => value + 1)
            URL.revokeObjectURL(objectUrl)
            return
          }

          const image = new ImageConstructor()

          const finalize = (cleanup?: () => void) => {
            image.onload = null
            image.onerror = null
            cleanup?.()
            triggerRerender((value) => value + 1)
          }

          image.onload = () => {
            if (
              image.naturalWidth >= MIN_IMAGE_ATTACHMENT_SIZE &&
              image.naturalHeight >= MIN_IMAGE_ATTACHMENT_SIZE
            ) {
              previewUrlsRef.current[item.id] = objectUrl
              finalize()
              return
            }

            excludedAttachmentIdsRef.current.add(item.id)
            finalize(() => {
              URL.revokeObjectURL(objectUrl)
            })
          }

          image.onerror = (error) => {
            if (controller.signal.aborted) {
              finalize(() => {
                URL.revokeObjectURL(objectUrl)
              })
              return
            }

            console.error('Failed to initialise image attachment preview', error)
            excludedAttachmentIdsRef.current.add(item.id)
            finalize(() => {
              URL.revokeObjectURL(objectUrl)
            })
          }

          image.src = objectUrl
        })
        .catch((error) => {
          if (controller.signal.aborted) {
            return
          }
          console.error('Failed to load image attachment preview', error)
        })
    })

    return () => {
      controllers.forEach((controller) => controller.abort())
    }
  }, [items])

  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url)
      })
      previewUrlsRef.current = {}
      excludedAttachmentIdsRef.current.clear()
    }
  }, [])

  if (!visibleItems.length) {
    return null
  }

  const containerClassName = classNames(sharedStyles.attachmentsWrapper, {
    [sharedStyles.dragEnabled]: hasHorizontalOverflow,
  })

  return (
    <div
      className={containerClassName}
      ref={containerRef}
    >
      <div className={styles.imageAttachments}>
        {visibleItems.map((item) => {
          const previewUrl = previewUrlsRef.current[item.id]

          return (
            <a
              key={`message-image-attachment-${item.id}`}
              className={styles.imageAttachment}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              draggable={false}
              onDragStart={handleImageAnchorDragStart}
              onClick={handleImageAnchorClick(item)}
              title={item.name}
            >
              {previewUrl ? (
                <AttachmentImage
                  url={previewUrl}
                  alt={item.name ?? item.url}
                  className={styles.imageAttachmentPreview}
                  template="external"
                />
              ) : null}
            </a>
          )
        })}
      </div>
    </div>
  )
}
